import {
  PublicTarotRequestStatus,
  TarotRequestCreate,
  TarotRequestResponse,
} from "@/types/tarot";
import { ErrorLogService } from "./errorLog";

// We will use Next.js server routes as a secure proxy and session manager.
// Public base is only used for build-time references; client calls go to /api/proxy.
const PROXY_BASE = "/api/proxy";

export class TarotApiService {
  private static async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const init: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      credentials: "include", // include cookie with token managed by server
    };

    // Ensure we have a session token cookie; try silent login on auth failure.
    // FastAPI (HTTPBearer) devolve 403 "Not authenticated" para token
    // ausente/expirado — não 401 — então os dois precisam renovar a sessão.
    const doFetch = async () => fetch(`${PROXY_BASE}${endpoint}`, init);
    let response = await doFetch();
    if (response.status === 401 || response.status === 403) {
      // attempt server-side login to set cookie, then retry once
      try {
        await fetch(`/api/session/login`, { method: "POST" });
        response = await doFetch();
      } catch {
        // ignore
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.detail || `HTTP ${response.status}: ${response.statusText}`
      );

      // Log API error to backend (don't await to avoid blocking)
      ErrorLogService.logApiError(endpoint, error, response.status, errorData);

      throw error;
    }
    return (await response.json()) as T;
  }

  // Auto-login now handled server-side; expose a no-op helper for callers
  static async autoLogin(): Promise<boolean> {
    try {
      const res = await fetch(`/api/session/login`, { method: "POST" });
      return res.ok;
    } catch (e) {
      console.error("Auto-login (server) failed:", e);
      return false;
    }
  }

  static async forceTokenRenewal(): Promise<boolean> {
    try {
      // Clear and re-login server-side
      await fetch(`/api/session/logout`, { method: "POST" });
      const res = await fetch(`/api/session/login`, { method: "POST" });
      return res.ok;
    } catch (error) {
      console.error("[ForceRenewal] Erro ao renovar token (server):", error);
      return false;
    }
  }

  // Authentication
  // Deprecated: credentials only on server. Keep a stub for compatibility.
  static async login(
    _username: string,
    _password: string
  ): Promise<{ access_token: string; token_type: string }> {
    void _username;
    void _password;
    await this.autoLogin();
    // Return a dummy structure to satisfy callers that previously expected a token
    return { access_token: "__cookie__", token_type: "Bearer" };
  }

  // Tarot Requests
  static async createTarotRequest(
    request: TarotRequestCreate
  ): Promise<TarotRequestResponse> {
    return this.request<TarotRequestResponse>("/tarot/requests", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  static async getTarotRequests(): Promise<TarotRequestResponse[]> {
    return this.request<TarotRequestResponse[]>("/tarot/requests");
  }

  static async getTarotRequest(id: number): Promise<TarotRequestResponse> {
    return this.request<TarotRequestResponse>(`/tarot/requests/${id}`);
  }

  static async getPublicTarotRequestStatus(
    token: string
  ): Promise<PublicTarotRequestStatus> {
    return this.request<PublicTarotRequestStatus>(
      `/tarot/requests/status/${encodeURIComponent(token)}`
    );
  }

  // Health check
  static async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>("/health");
  }

  static async checkBlockedEmailDomain(email: string): Promise<{
    domain: string;
    blocked: boolean;
  }> {
    const params = new URLSearchParams({ email });
    return this.request(`/blocked-email-domains/check?${params.toString()}`);
  }

  static async getPreviousPayerEmail(phone: string): Promise<string | null> {
    const result = await this.request<{ email: string | null }>(
      "/tarot/requests/previous-payer-email",
      { method: "POST", body: JSON.stringify({ phone }), signal: AbortSignal.timeout(2500) }
    );
    return result.email;
  }

  // Payment methods
  static async createPaymentIntent(paymentData: {
    amount: number;
    currency?: string;
    description?: string;
    customer_email?: string;
    metadata?: Record<string, string | number | boolean>;
  }): Promise<{
    payment_intent_id: string;
    client_secret: string;
    amount: number;
    currency: string;
    status: string;
    description?: string;
    created_at: string;
  }> {
    return this.request("/payments/create-intent", {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
  }

  // Stripe payment intent creation for existing tarot request
  static async createStripePaymentIntent(data: {
    request_id: number;
    amount_override_cents?: number;
  }): Promise<{
    payment_intent_id: string;
    client_secret: string;
    amount: number;
    currency: string;
    status: string;
  }> {
    return this.request("/payments/create-intent", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Confirm Stripe payment after successful payment
  static async confirmStripePayment(requestId: number): Promise<{
    success: boolean;
    payment_intent_id: string;
    status: string;
    execution_status: string;
  }> {
    return this.request(`/payments/stripe/confirm-payment/${requestId}`, {
      method: "POST",
    });
  }

  static async confirmPayment(confirmData: {
    payment_intent_id: string;
    tarot_request_data: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    payment_status: string;
    tarot_request_id?: number;
    message: string;
  }> {
    return this.request("/payments/confirm", {
      method: "POST",
      body: JSON.stringify(confirmData),
    });
  }

  // === NEW PAYMENT FLOW METHODS ===

  static async createTarotRequestAwaitingPayment(
    requestData: TarotRequestCreate
  ): Promise<TarotRequestResponse> {
    return this.request("/tarot/requests/awaiting-payment", {
      method: "POST",
      body: JSON.stringify(requestData),
    });
  }

  static async createPaymentIntentForRequest(requestId: number): Promise<{
    payment_intent_id: string;
    client_secret: string;
    amount: number;
  }> {
    return this.request(`/tarot/requests/${requestId}/payment-intent`, {
      method: "POST",
    });
  }

  static async confirmPaymentForRequest(
    requestId: number,
    paymentIntentId: string
  ): Promise<{
    success: boolean;
    message: string;
    request_id: number;
    execution_status: string;
  }> {
    const params = new URLSearchParams({ payment_intent_id: paymentIntentId });
    return this.request(
      `/tarot/requests/${requestId}/confirm-payment?${params.toString()}`,
      { method: "POST" }
    );
  }

  static async getPaymentStatusForRequest(requestId: number): Promise<{
    request_id: number;
    provider: "stripe" | "mercadopago" | "hotmart";
    status: string;
    amount?: number;
    currency?: string;
    stripe_payment_intent_id?: string;
    mp_preference_id?: string;
    mp_payment_id?: string;
    hotmart_transaction_id?: string;
  }> {
    return this.request(`/payments/status/${requestId}`);
  }

  static async createHotmartCheckout(requestId: number): Promise<{
    request_id: number;
    checkout_url: string;
    checkout_token: string;
  }> {
    return this.request(`/payments/hotmart/checkout/${requestId}`, {
      method: "POST",
    });
  }

  // Mercado Pago (non-wallet): process payment using data from the Payment Brick
  static async processMercadoPagoPaymentFromBrick(
    requestId: number,
    data: {
      selectedPaymentMethod: Record<string, unknown>;
      formData: Record<string, unknown>;
      payer_email?: string;
      amount_cents?: number;
    }
  ): Promise<{
    id: string | number;
    status: string;
    payment_method_id?: string;
    pix_qr_base64?: string;
    pix_qr_text?: string;
    pix_ticket_url?: string;
  }> {
    return this.request(`/payments/mercadopago/process`, {
      method: "POST",
      body: JSON.stringify({
        request_id: requestId,
        payer_email: data.payer_email,
        selected_payment_method: data.selectedPaymentMethod,
        form_data: data.formData,
        amount_cents: data.amount_cents,
      }),
    });
  }
}

// Client-side token storage removed; session is managed via HttpOnly cookie by server routes.
export const authUtils = {
  // Kept for backward compatibility; always returns null/false in new flow
  saveToken: (_token: string) => { void _token; },
  removeToken: () => {},
  getToken: () => null as string | null,
  isAuthenticated: () => false,
};
